import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import Toaster from 'components/Toaster';
import {
  PageShell,
  PageHeader,
  DataTable,
  SearchInput,
  AddButton,
  SegmentedFilter,
  ActionIcon,
  StatusPill,
  EntityCell,
  ConfirmModal,
  tokens,
} from 'components/v3';
import { query as GET_ACCOUNTS } from '../gql/queries/get-accounts';

const LIMIT = 15;

const DELETE_ACCOUNT = gql`
  mutation DeleteAccount($id: Int!) {
    deleteAccount(id: $id)
  }
`;

// SegmentedFilter key -> backend AccountsAdminFilter enum.
const FILTER_ENUM = { live: 'LIVE', active: 'ACTIVE', inactive: 'INACTIVE' };

const COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    width: 'minmax(240px,1.6fr)',
    primary: true,
    skeleton: 'media',
  },
  { key: 'contact', label: 'Primary Contact', width: 'minmax(160px,1fr)' },
  { key: 'properties', label: 'Properties', width: 'minmax(160px,1fr)' },
  { key: 'events', label: 'Events', width: '84px' },
  { key: 'status', label: 'Status', width: '120px', skeleton: 'pill' },
  {
    key: 'actions',
    label: 'Actions',
    width: '260px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 6,
  },
];

// Status: LIVE when there are published events this year, Inactive when the
// account's `inactive` flag is true, otherwise Active.
function renderStatus(account) {
  if (account.events_count > 0) {
    return (
      <StatusPill
        label="Live"
        color="#1F9D57"
        background="rgba(52,210,123,0.13)"
        border="rgba(52,210,123,0.34)"
      />
    );
  }
  if (account.inactive) {
    return (
      <StatusPill
        label="Inactive"
        color="#64748b"
        background="rgba(100,116,139,0.13)"
        border="rgba(100,116,139,0.34)"
      />
    );
  }
  return (
    <StatusPill
      label="Active"
      color="#2C6FD6"
      background="rgba(44,111,214,0.13)"
      border="rgba(44,111,214,0.34)"
    />
  );
}

const Accounts = () => {
  const t = tokens(useTheme());
  const [filter, setFilter] = useState('live');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmAccount, setConfirmAccount] = useState(null);
  const [toasterData, setToasterData] = useState(null);

  const input = {
    filter: FILTER_ENUM[filter],
    page: String(page),
    limit: LIMIT,
  };
  if (search) input.queryString = search;

  const { data, loading, refetch } = useQuery(GET_ACCOUNTS, {
    variables: { input },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [deleteAccount, { loading: deleting }] = useMutation(DELETE_ACCOUNT);

  const result = data && data.getAccounts;
  const accounts = (result && result.results) || [];
  const counts = (result && result.counts) || {
    live: 0,
    active: 0,
    inactive: 0,
  };
  const total = (result && result.count) || 0;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));

  const onFilter = (key) => {
    setFilter(key);
    setPage(1);
  };
  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleDelete = async () => {
    const account = confirmAccount;
    if (!account) return;
    const name = account.business_dba || account.legal_name;
    try {
      const res = await deleteAccount({
        variables: { id: parseInt(account.id, 10) },
      });
      if (res && res.data && res.data.deleteAccount) {
        setToasterData({
          title: 'Account deleted',
          message: `${name} has been deleted.`,
          type: 'success',
        });
        await refetch();
      } else {
        throw new Error('delete failed');
      }
    } catch (e) {
      setToasterData({
        title: 'Delete failed',
        message: `We couldn't delete ${name}. Please try again.`,
        type: 'error',
      });
    } finally {
      setConfirmAccount(null);
    }
  };

  const renderRow = (account) => {
    const title = account.business_dba || account.legal_name || '';
    return {
      name: (
        <EntityCell
          logo={account.logo}
          code={(title || '').slice(0, 3).toUpperCase()}
          title={title}
          subtitle={account.primary_contact || ''}
        />
      ),
      contact: account.primary_contact || '',
      properties: account.legal_name || '',
      events: (
        <span
          style={{
            fontSize: '14.5px',
            fontWeight: 600,
            color: account.events_count ? t.text : t.faint,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {account.events_count || 0}
        </span>
      ),
      status: renderStatus(account),
      actions: (
        <div style={{ display: 'flex', gap: 3 }}>
          <ActionIcon
            icon="events"
            title="Events"
            to={`/admin/account/${account.id}/events`}
          />
          <ActionIcon
            icon="calendar"
            title="Annuals"
            to={`/admin/accounts/${account.id}/annuals`}
          />
          <ActionIcon
            icon="staff"
            title="Staff"
            to={`/admin/accounts/editAccount/${account.id}?tab=staff`}
          />
          <ActionIcon
            icon="view"
            title="View"
            to={`/admin/accounts/reports/${account.id}`}
          />
          <ActionIcon
            icon="edit"
            title="Edit"
            to={`/admin/accounts/editAccount/${account.id}`}
          />
          <ActionIcon
            icon="delete"
            title="Delete"
            onClick={() => setConfirmAccount(account)}
          />
        </div>
      ),
    };
  };

  const confirmName = confirmAccount
    ? confirmAccount.business_dba || confirmAccount.legal_name
    : '';

  return (
    <PageShell>
      <PageHeader icon="users" title="Accounts" />

      <DataTable
        columns={COLUMNS}
        data={accounts}
        renderRow={renderRow}
        loading={loading}
        emptyLabel="No accounts in this view."
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        toolbar={
          <>
            <SearchInput
              onChange={onSearch}
              placeholder="Search accounts by name..."
              debounce={350}
            />
            <AddButton title="Add Account" to="/admin/accounts/addAccount" />
          </>
        }
        filter={
          <SegmentedFilter
            value={filter}
            onChange={onFilter}
            options={[
              {
                key: 'live',
                label: 'Live Events',
                count: counts.live,
                dot: true,
                dotColor: '#1F9D57',
              },
              { key: 'active', label: 'Active', count: counts.active },
              { key: 'inactive', label: 'Inactive', count: counts.inactive },
            ]}
          />
        }
      />

      <ConfirmModal
        open={!!confirmAccount}
        title="Delete this account?"
        message={
          confirmAccount
            ? `${confirmName} will be permanently deleted. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmAccount(null)}
      />

      {toasterData && (
        <Toaster
          isOpen
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          onClose={() => setToasterData(null)}
        />
      )}
    </PageShell>
  );
};

export default Accounts;
